import React from 'react';
import { animateScroll } from 'react-scroll';
import { Bundle, GalleryItemWithBundle } from 'types';
import NextSteps from 'components/NextSteps';
import SalesCalloutHTO from 'components/SalesCalloutHTO';
import SalesCalloutSwatches from 'components/SalesCalloutSwatches';
import GalleryFilter from 'partials/gallery/GalleryFilter';
import CloseButton from 'components/CloseButton';
import Modal from 'components/Modal';
import LightBoxItem from 'partials/gallery/LightBoxItem';
import tw from '../../tailwind.config';
import { withRouter } from 'next/router';
import { WithRouterProps } from 'next/dist/client/with-router';
import { getGalleryItems } from 'services/gallery';
import { withPageStaticProps } from 'utils/header';
import { GetStaticPropsContext } from 'next';
import { capitalizeFirstLetter } from '@root/src/utils/utils';
import { ColorFilter } from '@root/src/partials/visualizer';

const scrollRealNice = () => animateScroll.scrollToTop({ duration: 300 });

interface Props {
  bundles: Bundle[];
  items: GalleryItemWithBundle[];
  error: boolean;
}

interface State {
  lightBoxOpen: boolean;
  lightBoxItem: GalleryItemWithBundle;
  scrollPosition: number;
  colorFilters: ColorFilter[];
  styleFilters: string[];
  categoryFilters: string[];
}

class Gallery extends React.Component<Props & WithRouterProps, State> {
  constructor(props: Props & WithRouterProps) {
    super(props);

    this.state = {
      colorFilters: [],
      styleFilters: [],
      categoryFilters: [],
      lightBoxOpen: false,
      lightBoxItem: {} as GalleryItemWithBundle,
      scrollPosition: 0,
    };

    this.toggleColorFilters = this.toggleColorFilters.bind(this);
    this.toggleStyleFilters = this.toggleStyleFilters.bind(this);
    this.toggleCategoryFilters = this.toggleCategoryFilters.bind(this);
    this.handleLightboxClose = this.handleLightboxClose.bind(this);
    this.openLightboxOnPostPage = this.openLightboxOnPostPage.bind(this);
    this.closeLightboxOnIndex = this.closeLightboxOnIndex.bind(this);
  }

  closeLightboxOnIndex() {
    if (!this.state.lightBoxOpen || this.isPostPage()) {
      return;
    }

    this.setState(() => ({
      lightBoxOpen: false,
      lightBoxItem: {} as GalleryItemWithBundle,
    }));
  }

  componentDidMount() {
    this.openLightboxOnPostPage();

    this.jumpToGalleryItem();

    this.props.router.beforePopState(() => {
      this.openLightboxOnPostPage();
      this.closeLightboxOnIndex();
      return true;
    });
  }

  componentDidUpdate() {
    this.jumpToGalleryItem();
  }

  componentWillUnmount() {
    this.props.router.beforePopState(() => {
      return true;
    });
  }

  jumpToGalleryItem() {
    const param = this.props.router.pathname.replace('/gallery/post/', '');
    if (param) {
      const element = document.getElementById(`gallery-item-${param}`);
      if (element) {
        element.scrollIntoView();
      }
    }
  }

  toggleColorFilters(color: ColorFilter) {
    this.setState((state) => {
      const colors = state.colorFilters.includes(color) ? state.colorFilters.filter((c) => c !== color) : [...state.colorFilters, color];
      return { colorFilters: colors };
    });

    scrollRealNice();
  }

  toggleStyleFilters(style: string) {
    this.setState((state) => {
      const styles = state.styleFilters.includes(style) ? state.styleFilters.filter((s) => s !== style) : [...state.styleFilters, style];
      return { styleFilters: styles };
    });

    scrollRealNice();
  }

  toggleCategoryFilters(category: string) {
    this.setState((state) => {
      const categories = state.categoryFilters.includes(category)
        ? state.categoryFilters.filter((s) => s !== category)
        : [...state.categoryFilters, category];
      return { categoryFilters: categories };
    });

    scrollRealNice();
  }

  handleLightboxOpen = (item: GalleryItemWithBundle) => {
    this.setState(() => ({
      lightBoxOpen: true,
      lightBoxItem: item,
    }));

    this.props.router.push(`/gallery`, `/gallery/post/${item._id}`, { shallow: true });
  };

  handleLightboxClose() {
    this.setState(() => ({
      lightBoxOpen: false,
      lightBoxItem: {} as GalleryItemWithBundle,
    }));

    this.props.router.push(`/gallery`, undefined, { shallow: true });
  }

  isPostPage() {
    return this.props.router.asPath.includes('/post/');
  }

  getPostIdFromUrl() {
    const parts = this.props.router.asPath.split('/').filter((part) => part !== '');

    return parts.pop();
  }

  openLightboxOnPostPage() {
    if (this.state.lightBoxOpen || !this.isPostPage()) {
      return;
    }

    const found = this.props.items.find((b) => b._id === this.getPostIdFromUrl());

    if (found) {
      this.setState(() => ({
        lightBoxOpen: true,
        lightBoxItem: found!,
      }));
    } else {
      this.props.router.push(`/gallery`, undefined, { shallow: true });
    }
  }

  calculateHeight(ratio: string, width: number) {
    const [ratioWidth, ratioHeight] = ratio.split(':');
    return Math.round((width / parseInt(ratioWidth, 10)) * parseInt(ratioHeight, 10));
  }

  render() {
    return (
      <>
        <Modal
          showCloseButton={false}
          isOpen={this.state.lightBoxOpen}
          scrollPosition={this.state.scrollPosition}
          onClose={() => this.handleLightboxClose()}
          className={`${this.state.lightBoxOpen ? 'min-h-screen p-16 xs:p-32' : ''} !flex items-center justify-center`}
        >
          {this.state.lightBoxOpen && (
            <div className="container !p-0">
              <div className="relative animate-scaleIn shadow-3">
                <CloseButton onClose={() => this.handleLightboxClose()} className="absolute right-0" />

                <div className="row">
                  <div className="col-span-12 m-0 flex sm:col-span-6">
                    <div className="w-full">
                      <img
                        className="ml-auto max-h-[55vh] xs:max-h-[none] xs:w-full xs:object-cover"
                        style={{
                          display: 'block',
                          aspectRatio: `${this.state.lightBoxItem.image.asset.metadata?.dimensions?.width} / ${this.state.lightBoxItem.image.asset.metadata?.dimensions?.height}`,
                        }}
                        src={`${this.state.lightBoxItem.image.asset.url}?w=800&h=800&fm=jpg&fit=fillmax`}
                        alt={this.state.lightBoxItem.bundle.display_name}
                      />
                    </div>
                  </div>

                  <div className="col-span-12 m-0 flex flex-col px-32 pt-32 sm:col-span-6 md:px-64 md:pt-64 lg:px-128">
                    <div style={{ flexGrow: 1 }}>
                      <h2 className="text-h2-display mb-32">{this.state.lightBoxItem.bundle.display_name}</h2>

                      <div className="pb-32 md:pb-64">
                        <LightBoxItem lightBoxItem={this.state.lightBoxItem} error={this.props.error} />
                      </div>
                    </div>

                    <div className="pb-32">
                      {this.state.lightBoxItem.photoCredit?.toLowerCase() !== 'false' && (
                        <p className="text-sm text-gray">{this.state.lightBoxItem.photoCredit}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal>

        <div className="bg-gray-lighter">
          <div className="m-section-hero">
            <div className="container">
              <div className="row">
                <div className="col-span-12 sm:col-span-4">
                  <h1 className="text-h1">Gallery</h1>
                </div>

                <div className="col-span-12 sm:col-span-8">
                  <GalleryFilter
                    colors={['Blue', 'Black', 'Gray', 'White']}
                    colorFilters={this.state.colorFilters}
                    toggleColorFilters={(color) => this.toggleColorFilters(color as ColorFilter)}
                    styles={['suit', 'tux']}
                    styleFilters={this.state.styleFilters}
                    toggleStyleFilters={this.toggleStyleFilters}
                    categories={['Styled Shoot', 'Real Wedding']}
                    categoryFilters={this.state.categoryFilters}
                    toggleCategoryFilters={this.toggleCategoryFilters}
                  />
                </div>
              </div>
            </div>

            <div className="m-section col-count-1 xs:col-count-2 sm:col-count-3 md:col-count-5 lg:col-count-7 w-full gap-16 overflow-visible px-16 sm:gap-32 sm:px-32">
              {this.props.items
                .sort((a, b) => ('' + a.orderRank).localeCompare(b.orderRank ?? ''))
                .reduce((acc: GalleryItemWithBundle[], image: GalleryItemWithBundle) => {
                  const imageShouldBeFiltered = !(
                    (this.state.colorFilters.length === 0
                      ? true
                      : this.state.colorFilters.includes(capitalizeFirstLetter(image.color ?? '') as ColorFilter)) &&
                    (this.state.styleFilters.length === 0 ? true : this.state.styleFilters.includes(image.type ?? '')) &&
                    (this.state.categoryFilters.length === 0 ? true : this.state.categoryFilters.includes(image.category ?? ''))
                  );
                  if (imageShouldBeFiltered) {
                    return acc;
                  } else {
                    return [...acc, image];
                  }
                }, [])
                .map((image: GalleryItemWithBundle, key) => (
                  <div
                    className="translate-z-0 group transform cursor-pointer pb-32"
                    style={{ breakInside: 'avoid' }}
                    key={image._id}
                    id={`gallery-item-${image._id}`}
                    onClick={() => {
                      this.setState(() => ({ scrollPosition: window.scrollY }));
                      this.handleLightboxOpen(image);
                    }}
                  >
                    <picture>
                      <source
                        media={`(min-width: ${tw.theme.screens['lg']})`}
                        srcSet={`${image.image?.asset.url}?fit=crop&w=240&h=${this.calculateHeight(key % 3 === 0 ? '5:7' : '7:5', 240)}`}
                      />

                      <source
                        media={`(min-width: ${tw.theme.screens['md']})`}
                        srcSet={`${image.image?.asset.url}?fit=crop&w=217&h=${this.calculateHeight(key % 3 === 0 ? '5:7' : '7:5', 217)}`}
                      />

                      <source
                        media={`(min-width: ${tw.theme.screens['sm']})`}
                        srcSet={`${image.image?.asset.url}?fit=crop&w=288&h=${this.calculateHeight(key % 3 === 0 ? '5:7' : '7:5', 288)}`}
                      />

                      <img
                        className="ease-hover translate-z-0 block w-full transform transition duration-150 group-hover:shadow-4 group-hover:brightness-110 group-hover:filter"
                        style={{ willChange: 'box-shadow,filter' }}
                        src={`${image.image?.asset.url}?fit=crop&ar=${key % 3 === 0 ? '5:7' : '7:5'}&w=382&fm=jpg`}
                        alt={image.bundle.display_name}
                        loading="lazy"
                      />
                    </picture>

                    {image.photoCredit !== '' && <p className="text-sm text-gray-dark">{image.photoCredit}</p>}
                  </div>
                ))}
            </div>

            <NextSteps
              ctaPageName="gallery"
              salesCallout1={<SalesCalloutHTO ctaPageName="gallery" />}
              salesCallout2={<SalesCalloutSwatches ctaPageName="gallery" />}
            />
          </div>
        </div>
      </>
    );
  }
}

export default withRouter(Gallery);

export const getStaticProps = withPageStaticProps(async (context: GetStaticPropsContext) => {
  try {
    const items = await getGalleryItems();

    return {
      props: {
        items,
        error: false,
      },
    };
  } catch (error) {
    console.error('Failed to create gallery', { error });
    return {
      props: {
        error: true,
        bundles: [],
      },
    };
  }
});
